import React, { useState, useEffect } from 'react';
import { SenderView } from './components/SenderView';
import { ReceiverView } from './components/ReceiverView';
import { JoinView } from './components/JoinView';
import { WhisperMode } from './components/WhisperMode';
import logo from './logo.png';
import developerPhoto from './WhatsApp Image 2026-03-09 at 10,32,26 PM-photoaidcom-cropped.jpeg';


function App() {
  const [view, setView] = useState('home'); // 'home', 'sender', 'receiver', 'join', 'whisper'
  const [sessionId, setSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('heksta-theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply dark mode class
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('heksta-theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('heksta-theme', 'light');
    }
  }, [isDarkMode]);

  // Check if we're joining a session via URL
  useEffect(() => {
    const path = window.location.pathname;
    const sessionIdMatch = path.match(/^\/join\/([a-zA-Z0-9]{8})$/);

    if (sessionIdMatch) {
      setSessionId(sessionIdMatch[1]);
      setView('join');
    }
  }, []);

  // Handle browser navigation
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/') {
        setView('home');
        setSessionId(null);
        setSessionData(null);
      } else if (path.startsWith('/join/')) {
        const sessionIdMatch = path.match(/^\/join\/([a-zA-Z0-9]{8})$/);
        if (sessionIdMatch) {
          setSessionId(sessionIdMatch[1]);
          setView('join');
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Handle PWA installation
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setIsAppInstalled(true);
      console.log('Heksta was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsAppInstalled(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;

    // Show the install prompt
    installPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await installPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);

    // We've used the prompt, and can't use it again, throw it away
    setInstallPrompt(null);
  };

  // Update URL
  useEffect(() => {
    switch (view) {
      case 'home': window.history.pushState({}, '', '/'); break;
      case 'sender': window.history.pushState({}, '', sessionId ? `/sender/${sessionId}` : '/sender'); break;
      case 'join': window.history.pushState({}, '', sessionId ? `/join/${sessionId}` : '/join'); break;
      case 'receiver': if (sessionId) window.history.pushState({}, '', `/join/${sessionId}`); break;
      case 'whisper': window.history.pushState({}, '', '/whisper'); break;
    }
  }, [view, sessionId]);

  const handleBackToHome = () => {
    setView('home');
    setSessionId(null);
    setSessionData(null);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: 'var(--premium-bg)'}}>
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{backgroundColor: 'color-mix(in srgb, var(--surface) 90%, transparent)', borderColor: 'var(--border)', boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => { handleBackToHome(); setMobileMenuOpen(false); }}>
              <img src={logo} alt="Heksta Logo" className="h-9 md:h-10 w-auto dark:brightness-0 dark:invert" />
              <div className="hidden sm:block">
                <span className="text-[10px] block font-medium tracking-wider uppercase" style={{color: 'var(--text-muted)'}}>Universal Network Support</span>
              </div>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-500">
              <a href="#features" className="nav-link">Features</a>
              <a href="#about" className="nav-link">About</a>
              <a href="#how-it-works" className="nav-link">How it Works</a>
              <a href="#whisper" onClick={(e) => { e.preventDefault(); setView('whisper'); }} className="nav-link !text-emerald-600 hover:!text-emerald-700 font-semibold flex items-center gap-1.5">🤫 Whisper <span className="text-[9px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-full font-bold border border-emerald-100">BETA</span></a>
              <a href="#developer" className="nav-link">Developer</a>
              <a href="#join" className="nav-link">Join</a>
            </div>

            <div className="flex items-center gap-3">
              {!isAppInstalled && (
                <button
                  onClick={() => installPrompt ? handleInstallClick() : setShowInstallModal(true)}
                  className="hidden sm:flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/30 text-[#1a4d2e] dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 px-4 py-2 rounded-xl text-sm font-bold border border-emerald-100 dark:border-emerald-800/40 transition-all"
                >
                  <span className="text-base">📲</span> Install App
                </button>
              )}
              {/* Theme Toggle Button - Made more prominent */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-all bg-slate-100 dark:bg-slate-800 hover:scale-110 active:scale-95 text-xl shadow-sm border border-slate-200 dark:border-slate-700"
                title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDarkMode ? '☀️' : '🌙'}
              </button>

              {view !== 'home' ? (
                <button onClick={() => { handleBackToHome(); setMobileMenuOpen(false); }} className="btn-secondary text-sm py-2">← Home</button>
              ) : (
                <button onClick={() => setView('sender')} className="btn-primary text-sm py-2 px-5">Get Started</button>
              )}
              {/* Hamburger Button */}
              <button
                onClick={() => setMobileMenuOpen(o => !o)}
                className="md:hidden flex flex-col justify-center items-center w-9 h-9 gap-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Toggle menu"
              >
                <span className={`block w-5 h-0.5 bg-slate-700 dark:bg-slate-300 transition-all duration-300 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                <span className={`block w-5 h-0.5 bg-slate-700 dark:bg-slate-300 transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
                <span className={`block w-5 h-0.5 bg-slate-700 dark:bg-slate-300 transition-all duration-300 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden border-t py-4 px-2 flex flex-col gap-1 animate-fade-in" style={{backgroundColor: 'var(--surface)', borderColor: 'var(--border)'}}>
              <a href="#features" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-4 py-3 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" style={{color: 'var(--text-primary)'}}>Features</a>
              <a href="#about" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-4 py-3 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" style={{color: 'var(--text-primary)'}}>About</a>
              <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-4 py-3 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" style={{color: 'var(--text-primary)'}}>How it Works</a>
              <a
                href="#whisper"
                onClick={(e) => { e.preventDefault(); setView('whisper'); setMobileMenuOpen(false); }}
                className="rounded-lg px-4 py-3 text-sm font-bold text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors flex items-center gap-2"
              >🤫 Whisper Mode <span className="text-[10px] bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 rounded-full text-emerald-700 dark:text-emerald-400">BETA</span></a>
              <a href="#developer" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-4 py-3 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" style={{color: 'var(--text-primary)'}}>Developer</a>
              <a href="#join" onClick={() => { setView('join'); setMobileMenuOpen(false); }} className="rounded-lg px-4 py-3 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" style={{color: 'var(--text-primary)'}}>Join Session</a>
              <button
                onClick={() => { setIsDarkMode(!isDarkMode); setMobileMenuOpen(false); }}
                className="flex items-center justify-between gap-3 rounded-xl px-4 py-4 text-sm font-bold bg-slate-100 dark:bg-slate-800 transition-all active:scale-95 mb-2"
                style={{color: 'var(--text-primary)'}}
              >
                <div className="flex items-center gap-3">
                  <span>{isDarkMode ? '☀️' : '🌙'}</span>
                  <span>{isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}</span>
                </div>
                <div className={`w-10 h-5 rounded-full relative transition-colors ${isDarkMode ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                  <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${isDarkMode ? 'right-1' : 'left-1'}`}></div>
                </div>
              </button>
              {!isAppInstalled && (
                <button
                  onClick={() => { installPrompt ? handleInstallClick() : setShowInstallModal(true); setMobileMenuOpen(false); }}
                  className="mt-2 flex items-center justify-center gap-2 bg-[#1a4d2e] text-white px-4 py-4 rounded-xl text-sm font-bold shadow-lg shadow-[#1a4d2e]/20"
                >
                  📲 Install Heksta PWA
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      <main className="pt-20">
        {view === 'home' && (
          <>
            {/* Hero Section */}
            <section className="relative overflow-hidden py-20 lg:py-28 hero-gradient">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <div className="badge mb-5 animate-fade-in">v2.0.2 Now Live</div>
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold mb-6 leading-[1.1] tracking-tight animate-fade-in-up" style={{color: 'var(--text-primary)'}}>
                  Seamless <span className="heading-gradient">File Broadcasting</span><br className="hidden sm:block" /><span className="sm:hidden"> </span>for Teams.
                </h1>
                <p className="max-w-xl mx-auto text-base sm:text-lg mb-10 leading-relaxed animate-fade-in-up delay-100 text-balance" style={{color: 'var(--text-secondary)'}}>
                  Broadcast large datasets, media, and files across the web instantly — with zero configuration and total privacy.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center animate-fade-in-up delay-200">
                  <div className="theme-green w-full sm:w-auto">
                    <button onClick={() => setView('sender')} className="btn-primary text-base w-full sm:px-8 py-3.5">Start Broadcasting →</button>
                  </div>
                  <div className="theme-orange w-full sm:w-auto">
                    <button onClick={() => setView('join')} className="btn-primary text-base w-full sm:px-8 py-3.5">Join Session</button>
                  </div>
                </div>
                <div className="mt-10 flex flex-wrap justify-center gap-6 text-xs sm:text-sm font-medium animate-fade-in-up delay-300" style={{color: 'var(--text-muted)'}}>
                  <span className="flex items-center gap-1.5"><span className="text-emerald-500">✓</span> Global Internet Reach</span>
                  <span className="flex items-center gap-1.5"><span className="text-emerald-500">✓</span> End-to-End Secure</span>
                  <span className="flex items-center gap-1.5"><span className="text-emerald-500">✓</span> Up to 5GB Per Session</span>
                </div>
              </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-20" style={{backgroundColor: 'var(--surface)'}}>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-14">
                  <span className="section-title mb-3 block">Core Features</span>
                  <h2 className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight" style={{color: 'var(--text-primary)'}}>Engineered for Universal Support</h2>
                  <p className="max-w-lg mx-auto text-sm leading-relaxed" style={{color: 'var(--text-secondary)'}}>Built on robust protocols, Heksta ensures your files move faster than ever across any network.</p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[{icon:'🌐',title:'Universal Network',desc:'Auto-discovers best routing and optimizes for maximum throughput on any connection.'},{icon:'📡',title:'One-to-Many',desc:'One sender can serve hundreds of receivers simultaneously without compromising speed.'},{icon:'🛡️',title:'Privacy by Design',desc:'No cloud storage, no third-party servers, and zero telemetry. Complete data sovereignty.'},{icon:'📂',title:'Full Folder Support',desc:'Preserve your folder hierarchy. Receivers download everything as a structured ZIP.'},{icon:'⚡',title:'5GB Transfers',desc:'High-performance chunked streaming at wire-link speeds with smart buffering.'}].map((f,i)=>(
                    <div key={i} className="card-hover group p-6">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl mb-4" style={{background:'var(--primary-50)'}}>{f.icon}</div>
                      <h3 className="text-base font-bold mb-2 group-hover:text-[var(--primary)] transition-colors" style={{color: 'var(--text-primary)'}}>{f.title}</h3>
                      <p className="text-sm leading-relaxed" style={{color: 'var(--text-secondary)'}}>{f.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-24" style={{backgroundColor: 'var(--premium-bg)'}}>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  <div>
                    <h2 className="text-3xl font-bold mb-8" style={{color: 'var(--text-primary)'}}>What is Heksta?</h2>
                    <div className="space-y-6 leading-relaxed" style={{color: 'var(--text-secondary)'}}>
                      <p>In an era dominated by cloud synchronization, we often forget that the fastest connection is the one right next to us. Heksta was born out of the necessity for a high-performance, resilient, and simple way to bridge the gap between devices on the same network. It isn't just a file sharing tool; it's a Universal Network Support layer that abstracts away the complexities of local discovery and data transmission.</p>
                      <p>Traditionally, sharing a 10GB file with a room full of people required either expensive specialized hardware or slow, bandwidth-heavy cloud uploads that eventually came back down to everyone else. Heksta flips this paradigm. By leveraging the full potential of your Universal Network Support, Heksta allows a single "Sender" to broadcast files directly to multiple "Receivers" simultaneously at wire speeds.</p>
                      <p><strong>The Purpose:</strong> Our mission is to democratize high-speed data transfer across the internet. Whether you are a film crew needing to distribute daily rushes globally, a teacher sharing resources with students, or a developer deploying assets, Heksta provides a robust platform that "just works" using your existing internet connection. No barriers, just seamless sharing of your file data and all.</p>
                      <p><strong>Key Features & Advantages:</strong> Unlike standard P2P tools that focus on 1-to-1 transfers, Heksta is optimized for 1-to-N broadcasting. It utilizes smart buffering and chunked transmission to ensure that even low-power devices can stay in sync with the stream. With our "Universal Network Support," Heksta intelligently adapts to the specific constraints of your network—be it Ethernet, Wi-Fi 6, or even legacy 2.4GHz connections—to provide the best possible experience without manual tuning.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {[{val:'0%',label:'Cloud Dependency'},{val:'∞',label:'Transfer Limit'},{val:'<1ms',label:'Local Latency'},{val:'AES',label:'Encryption'}].map((s,i)=>(
                      <div key={i} className="glass-card text-center py-8 px-4 hover:shadow-lg transition-all duration-300">
                        <div className="text-2xl sm:text-3xl font-bold text-[var(--primary)] mb-1.5">{s.val}</div>
                        <div className="text-[10px] font-bold uppercase tracking-widest" style={{color: 'var(--text-muted)'}}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Workflow Section */}
            <section id="how-it-works" className="py-24" style={{backgroundColor: 'var(--surface)'}}>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                  <h2 className="text-3xl font-bold mb-4" style={{color: 'var(--text-primary)'}}>Seamless Workflow</h2>
                </div>
                <div className="max-w-4xl mx-auto space-y-12">
                  <div className="flex gap-8 items-start">
                    <div className="h-12 w-12 rounded-full bg-[#1a4d2e] text-white flex-shrink-0 flex items-center justify-center font-bold text-xl">1</div>
                    <div>
                      <h3 className="text-xl font-bold mb-2" style={{color: 'var(--text-primary)'}}>Initialize Broadcast</h3>
                      <p style={{color: 'var(--text-secondary)'}}>The Sender selects files and Heksta creates a localized secure broadcast session.</p>
                    </div>
                  </div>
                  <div className="flex gap-8 items-start border-l-2 border-dashed border-slate-100 dark:border-slate-800 pl-8 ml-6">
                    <div className="h-12 w-12 rounded-full bg-[#1a4d2e] text-white flex-shrink-0 flex items-center justify-center font-bold text-xl">2</div>
                    <div>
                      <h3 className="text-xl font-bold mb-2" style={{color: 'var(--text-primary)'}}>Distribute Access</h3>
                      <p style={{color: 'var(--text-secondary)'}}>Share the dynamic QR code or Session ID with everyone on your network.</p>
                    </div>
                  </div>
                  <div className="flex gap-8 items-start border-l-2 border-dashed border-slate-100 dark:border-slate-800 pl-8 ml-6">
                    <div className="h-12 w-12 rounded-full bg-[#1a4d2e] text-white flex-shrink-0 flex items-center justify-center font-bold text-xl">3</div>
                    <div>
                      <h3 className="text-xl font-bold mb-2" style={{color: 'var(--text-primary)'}}>High-Speed Sync</h3>
                      <p style={{color: 'var(--text-secondary)'}}>Receivers connect instantly and pull data at the maximum speed of the network.</p>
                    </div>
                  </div>
                </div>
                <div className="mt-16 text-center">
                  <button onClick={() => setView('sender')} className="btn-primary">Create Your First Session</button>
                </div>
              </div>
            </section>
          </>
        )}

        {view === 'sender' && (
          <SenderView
            sessionId={sessionId}
            sessionData={sessionData}
            onSessionCreated={(data) => {
              setSessionId(data.sessionId);
              setSessionData(data);
              setView('sender');
            }}
          />
        )}

        {view === 'join' && (
          <JoinView
            sessionId={sessionId}
            onSessionJoined={(data) => {
              setSessionData(data);
              setSessionId(data.sessionId || sessionId);
              setView('receiver');
            }}
          />
        )}

        {view === 'receiver' && (
          <ReceiverView
            sessionId={sessionId}
            sessionData={sessionData}
          />
        )}

        {view === 'whisper' && (
          <div className="theme-green">
            <WhisperMode />
          </div>
        )}

        {view === 'home' && (
          <section id="developer" className="py-24 border-t" style={{backgroundColor: 'var(--premium-bg)', borderColor: 'var(--border)'}}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col items-center text-center">
                <div className="mb-8 relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#1a4d2e] to-emerald-400 rounded-full blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                  <img
                    src={developerPhoto}
                    alt="Sujal Patel"
                    className="relative w-32 h-32 rounded-full border-4 shadow-xl object-cover"
                    style={{borderColor: 'var(--surface)'}}
                  />
                </div>
                <h2 className="text-3xl font-bold mb-2" style={{color: 'var(--text-primary)'}}>Sujal Patel</h2>
                <p className="text-[#1a4d2e] dark:text-emerald-400 font-semibold mb-6">Lead Developer & Visionary</p>

                <div className="flex flex-wrap justify-center gap-4">
                  <a href="https://www.instagram.com/sujalpatel_2606" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 rounded-xl shadow-sm border hover:border-pink-500 hover:text-pink-600 transition-all duration-300" style={{backgroundColor: 'var(--surface)', borderColor: 'var(--border)'}}>
                    <svg className="w-5 h-5 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.266.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                    <span className="font-semibold text-sm">sujalpatel_2606</span>
                  </a>
                  <a href="https://www.linkedin.com/in/sujal1" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 rounded-xl shadow-sm border hover:border-[#0077b5] hover:text-[#0077b5] transition-all duration-300" style={{backgroundColor: 'var(--surface)', borderColor: 'var(--border)'}}>
                    <svg className="w-5 h-5 text-[#0077b5]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                    <span className="font-semibold text-sm">LinkedIn Profile</span>
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start gap-10 mb-10">
            <div className="max-w-xs">
              <img src={logo} alt="Heksta Logo" className="h-7 w-auto brightness-0 invert mb-4" />
              <p className="text-xs leading-relaxed text-slate-500">High-performance file broadcasting for teams who care about speed, security, and privacy.</p>
            </div>
            <div className="flex gap-12 text-xs">
              <div>
                <h3 className="text-white font-semibold mb-3 text-xs">Product</h3>
                <ul className="space-y-2"><li><a href="#features" className="hover:text-white transition-colors">Features</a></li><li><a href="#how-it-works" className="hover:text-white transition-colors">Workflow</a></li></ul>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-3 text-xs">Connect</h3>
                <ul className="space-y-2"><li><a href="#developer" className="hover:text-white transition-colors">Developer</a></li><li><a href="#" className="hover:text-white transition-colors">GitHub</a></li></ul>
              </div>
            </div>
          </div>
          <div className="pt-6 border-t flex flex-col sm:flex-row justify-between items-center gap-4" style={{borderColor: 'var(--border)'}}>
            <p className="text-[11px]" style={{color: 'var(--text-muted)'}}>© 2026 Heksta. All rights reserved.</p>
            <div className="flex gap-6 text-[11px]"><a href="#" className="hover:text-white transition-colors">Terms</a><a href="#" className="hover:text-white transition-colors">Privacy</a><a href="#" className="hover:text-white transition-colors">MIT License</a></div>
          </div>
        </div>
      </footer>
      {/* Install App Modal (for iOS/Others) */}
      {showInstallModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="rounded-3xl p-8 max-w-sm w-full shadow-2xl relative" style={{backgroundColor: 'var(--surface)'}}>
            <button onClick={() => setShowInstallModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">✕</button>
            <div className="text-center">
              <div className="bg-emerald-50 dark:bg-emerald-900/30 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">📱</span>
              </div>
              <h2 className="text-xl font-bold mb-2" style={{color: 'var(--text-primary)'}}>Install Heksta App</h2>
              <p className="text-sm mb-6 leading-relaxed" style={{color: 'var(--text-secondary)'}}>Run Heksta offline directly from your home screen like a native app.</p>

              <div className="space-y-4 text-left">
                <div className="flex gap-4 items-start p-3 bg-slate-50 rounded-xl">
                  <span className="bg-white w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold border border-slate-100 italic">i</span>
                  <div>
                    <p className="text-xs font-bold text-slate-800 mb-1 leading-tight">For iPhone (Safari)</p>
                    <p className="text-[10px] text-slate-500 leading-normal">Tap <span className="font-bold text-slate-700">Share</span> button and select <span className="font-bold text-slate-700">"Add to Home Screen"</span></p>
                  </div>
                </div>
                <div className="flex gap-4 items-start p-3 bg-slate-50 rounded-xl">
                  <span className="bg-white w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold border border-slate-100 italic">A</span>
                  <div>
                    <p className="text-xs font-bold text-slate-800 mb-1 leading-tight">For Android (Chrome)</p>
                    <p className="text-[10px] text-slate-500 leading-normal">Tap <span className="font-bold text-slate-700">Menu (⋮)</span> and select <span className="font-bold text-slate-700">"Install App"</span></p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowInstallModal(false)}
                className="btn-primary w-full py-3 mt-8 text-sm"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
